import React from 'react';
import { create } from 'zustand';

import { Customer } from '@blocks/types/customer';
import { Session } from '@blocks/types/session';

import { deleteSession, getCustomer, getSession } from '../api';
import { AUTH_EVENT_NAME, LOGOUT_EVENT, STORAGE_ORDER, USER_ROLE_CUSTOMER } from '../constants';
import { dispatchCustomEvent, isBrowser } from '../utils';
import { getPageType } from '../utils/url';

type SessionStore = {
  isFetching: boolean;
  session: Partial<Session>;
  customer: Partial<Customer>;
  fetchSession: (domain: string) => void;
  logout: (domain: string) => void;
};

const useSession = create<SessionStore>((set, get) => ({
  isFetching: false,
  customer: {},
  session: {},
  fetchSession: async (domain: string) => {
    if (isBrowser()) {
      const { isFetching, session } = get();
      if (!Object.keys(session).length && !isFetching) {
        set({ isFetching: true });
        const res = await getSession({ domain });
        set({ session: res });
        if (res.user_role === USER_ROLE_CUSTOMER) {
          const res = await getCustomer(domain);
          const customerData = {
            ...res,
            pageType: getPageType(),
          };
          set({ customer: customerData, isFetching: false });
        } else {
          set({ isFetching: false });
        }
      }
    }
  },
  logout: async (domain: string) => {
    try {
      await deleteSession({ domain });
      set({ session: {}, customer: {} });
      localStorage.removeItem(STORAGE_ORDER);
    } catch (error) {
      console.error(error);
    }
  },
}));

export const useSessionStore = (domain: string) => {
  const { fetchSession, ...rest } = useSession();

  React.useEffect(() => {
    fetchSession(domain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...rest };
};

export const createLogout =
  ({ domain, onLogout = () => {} }: { domain: string; onLogout: (...args: any) => void }) =>
  () => {
    useSession.getState().logout(domain);
    onLogout(useSession.getState().customer);
    dispatchCustomEvent(AUTH_EVENT_NAME, { user_role: undefined });
    dispatchCustomEvent(LOGOUT_EVENT);
  };
